import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet-rotate'; // adauga rotirea hartii (setBearing) la Leaflet - pentru modul "navigatie"
import 'leaflet/dist/leaflet.css';

// Leaflet incarca implicit iconitele markerelor dintr-un CDN care nu merge bine
// cu bundler-ul CRA (webpack rescrie path-urile din pachet) - folosim iconite
// simple, desenate ca SVG inline, ca sa nu depindem de nimic extern.
function creazaIcon(culoare) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:16px;height:16px;border-radius:50%;
      background:${culoare};border:2px solid #fff;
      box-shadow:0 0 4px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}
const ICON_START = creazaIcon('#1a3a5c');

// Paleta fixa de culori usor de distins intre ele pe harta (nu prea apropiate una de
// alta, nici prea deschise pe fond de OpenStreetMap). Reciclata daca sunt mai multe
// curse active simultan decat culori.
const PALETA_CULORI = [
  '#1a7a3a', '#c0392b', '#8e44ad', '#e67e22',
  '#2980b9', '#16a085', '#d35400', '#7f2c8f',
  '#27ae60', '#2c3e50',
];

// Culoarea unui vehicul trebuie sa ramana STABILA intre reimprospatari (harta se
// reincarca la fiecare 5s la dispecer) - nu putem doar sa luam index-ul din array,
// fiindca ordinea curselor active se poate schimba de la o reimprospatare la alta.
// Alocam deci pe baza unui hash simplu si stabil al identificatorului vehiculului
// (nrInmatriculare, sau eticheta primita), nu pe pozitia din lista.
function culoarePentru(cheie) {
  if (!cheie) return PALETA_CULORI[0];
  let hash = 0;
  for (let i = 0; i < cheie.length; i++) {
    hash = (hash * 31 + cheie.charCodeAt(i)) >>> 0;
  }
  return PALETA_CULORI[hash % PALETA_CULORI.length];
}

// ---- Modul "navigatie" (directia de mers in sus, ca la GPS-ul din masina) ----
const ZOOM_NAVIGATIE = 17;
// leaflet-rotate roteste harta in sens ORAR cu setBearing(unghi). Pentru ca directia de
// mers sa fie "in sus", harta trebuie rotita invers: 360 - directie. Daca pe telefon harta
// se invarte in sensul gresit, schimba valoarea de mai jos in true.
const INVERSEAZA_ROTATIA = false;

function metri(a, b) {
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLon = (b.lon - a.lon) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Directia (grade, 0 = nord, in sens orar) de la punctul a la punctul b.
function directie(a, b) {
  const f1 = a.lat * Math.PI / 180;
  const f2 = b.lat * Math.PI / 180;
  const dl = (b.lon - a.lon) * Math.PI / 180;
  const y = Math.sin(dl) * Math.cos(f2);
  const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dl);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

// Directia de mers din coada traseului: ultimul punct fata de un punct anterior aflat la cel
// putin 20 m (mai aproape, zgomotul GPS ar roti harta haotic). Null daca nu s-a miscat destul.
function directieDinPuncte(puncte) {
  if (!puncte || puncte.length < 2) return null;
  const ultim = puncte[puncte.length - 1];
  for (let i = puncte.length - 2, pasi = 0; i >= 0 && pasi < 15; i--, pasi++) {
    if (metri(puncte[i], ultim) >= 20) return directie(puncte[i], ultim);
  }
  return null;
}

// ---- Goluri de semnal GPS ----
// Doua puncte consecutive la >= 60 s si >= 200 m una de alta = in acest interval telefonul n-a avut semnal
// (tunel, zona fara semnal). Daca dupa pierdere citirile revin in aceeasi zona (< 200 m - ai stat intr-o
// cladire / parcare), NU e gol: traseul ramane continuu. La un gol linia nu se mai deseneaza plina
// (n-avem puncte intre ele): apare doar o linie punctata, ca sa se vada ca e o estimare.
export const GOL_MIN_SECUNDE = 60;
export const GOL_MIN_METRI = 200;

function timpMs(p) {
  const t = Date.parse(p.ts || p.timestamp);
  return Number.isNaN(t) ? null : t;
}

export function esteGol(a, b) {
  const ta = timpMs(a);
  const tb = timpMs(b);
  if (ta == null || tb == null) return false;
  return (tb - ta) / 1000 >= GOL_MIN_SECUNDE && metri(a, b) >= GOL_MIN_METRI;
}

// Imparte traseul in bucati continue + intervalele de gol dintre ele.
function imparteLaGoluri(puncte) {
  const bucati = [];
  const goluri = [];
  let curenta = [];
  for (let i = 0; i < puncte.length; i++) {
    if (i > 0 && esteGol(puncte[i - 1], puncte[i])) {
      bucati.push(curenta);
      goluri.push([[puncte[i - 1].lat, puncte[i - 1].lon], [puncte[i].lat, puncte[i].lon]]);
      curenta = [];
    }
    curenta.push([puncte[i].lat, puncte[i].lon]);
  }
  bucati.push(curenta);
  return { bucati, goluri };
}

const STIL_BUTON = {
  background: '#fff', border: '1px solid #888', borderRadius: 4, padding: '6px 10px',
  fontSize: 13, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
};

// Harta simpla, cu tile-uri OpenStreetMap (gratuit, fara API key).
// props:
//   trasee: array de { id, puncte:[{lat,lon}], culoare, eticheta, pozitieCurenta:{lat,lon} }
//   inaltime: inaltimea in px a hartii
//   permiteNavigatie: arata butonul "Nord sus / Navigatie" (modul navigatie urmareste PRIMUL traseu)
//   navigatieImplicit: porneste direct in modul navigatie
//
// Vederea hartii nu mai este resetata la fiecare actualizare: harta se incadreaza pe traseu
// o singura data (la primele date), iar dupa ce utilizatorul da zoom / trage de harta ramane
// unde a lasat-o. Butonul "Incadreaza" (sau "Recentreaza" in modul navigatie) reia vederea automata.
export default function CursaGpsMap({
  trasee = [], inaltime = 320, permiteNavigatie = false, navigatieImplicit = false,
  ruta = null, destinatie = null, plinLaMontare = false, inchideEcranComplet = false, suprapunere = null,
  pozitieLive = null, // {lat,lon}: pozitia cursorului venita LOCAL din telefon (fara server); muta doar cursorul
}) {
  const liveRef = useRef(null);
  const liveMarkerRef = useRef(null);
  const markerTraseuRef = useRef(null); // cursorul desenat din punctele de pe server (primul traseu)
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);

  const modNavRef = useRef(permiteNavigatie && navigatieImplicit);
  const urmaresteRef = useRef(true);   // in modul navigatie: harta urmareste vehiculul
  const autoFitRef = useRef(true);     // in modul nord: harta se incadreaza singura pe traseu
  const zoomNavSetatRef = useRef(false);
  const directieRef = useRef(null);
  const dateRef = useRef({ toate: [], ultimaPoz: null, puncteVehicul: [] });

  const [modNav, setModNav] = useState(modNavRef.current);
  const [urmareste, setUrmareste] = useState(true);
  const [plin, setPlin] = useState(!!plinLaMontare); // harta maximizata pe tot ecranul (la Start: implicit)

  // Dialogurile aplicatiei (ex. km de la bord la Stop) apar sub harta maximizata - parintele cere
  // inchiderea ecranului complet cat timp un asemenea dialog e deschis.
  useEffect(() => { if (inchideEcranComplet) setPlin(false); }, [inchideEcranComplet]);

  // La (de)maximizare harta isi recalculeaza dimensiunea; cat e maximizata, pagina din spate nu
  // deruleaza, iar Esc o inchide.
  useEffect(() => {
    const t = setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 80);
    if (!plin) return () => clearTimeout(t);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setPlin(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener('keydown', onKey);
    };
  }, [plin]);

  function seteazaModNav(valoare) {
    modNavRef.current = valoare;
    setModNav(valoare);
  }
  function seteazaUrmareste(valoare) {
    urmaresteRef.current = valoare;
    setUrmareste(valoare);
  }

  // Pozitioneaza harta conform modului curent. Foloseste doar ref-uri (nu state), ca sa poata
  // fi apelata atat din efectul de desenare, cat si din butoane, fara valori invechite.
  function aplicaVedere() {
    const map = mapRef.current;
    if (!map) return;
    const { toate, ultimaPoz, puncteVehicul } = dateRef.current;

    if (modNavRef.current) {
      if (!ultimaPoz) return;
      const dir = directieDinPuncte(puncteVehicul);
      if (dir != null) directieRef.current = dir;
      if (!urmaresteRef.current) return;
      const zoom = zoomNavSetatRef.current ? map.getZoom() : ZOOM_NAVIGATIE;
      zoomNavSetatRef.current = true;
      map.setView([ultimaPoz.lat, ultimaPoz.lon], zoom, { animate: false });
      if (directieRef.current != null && typeof map.setBearing === 'function') {
        const unghi = INVERSEAZA_ROTATIA ? directieRef.current : (360 - directieRef.current) % 360;
        map.setBearing(unghi);
      }
      return;
    }

    if (autoFitRef.current && toate.length > 0) {
      map.fitBounds(toate, { padding: [30, 30], maxZoom: 16 });
      autoFitRef.current = false; // de acum, vederea ramane a utilizatorului
    }
  }

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const container = containerRef.current;
    const map = L.map(container, {
      zoomControl: true,
      rotate: true,           // leaflet-rotate
      bearing: 0,
      touchRotate: false,     // rotirea o face modul navigatie, nu degetele (evita rotiri accidentale)
      shiftKeyRotate: false,
      rotateControl: false,
    }).setView([45.9432, 24.9668], 7); // centrat pe Romania implicit
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerGroupRef.current = L.layerGroup().addTo(map);

    // Orice atingere a hartii de catre utilizator opreste vederea automata (altfel actualizarile
    // ar trage harta inapoi in timp ce o muta). Miscarile facute din cod nu declanseaza evenimente
    // de mouse/atingere, deci nu se confunda cu cele ale utilizatorului. Butoanele +/- raman
    // compatibile cu urmarirea (zoom-ul ales se pastreaza), dar opresc incadrarea automata.
    const dinUtilizator = (e) => {
      autoFitRef.current = false;
      const peControl = e.target && e.target.closest && e.target.closest('.leaflet-control');
      if (!peControl && modNavRef.current && urmaresteRef.current) {
        urmaresteRef.current = false;
        setUrmareste(false);
      }
    };
    const evenimente = ['pointerdown', 'wheel', 'dblclick'];
    evenimente.forEach(ev => container.addEventListener(ev, dinUtilizator, { passive: true }));

    return () => {
      evenimente.forEach(ev => container.removeEventListener(ev, dinUtilizator));
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Se redeseneaza doar cand s-au schimbat datele (nu la fiecare re-randare a parintelui,
  // care primeste un obiect nou la fiecare randare) - semnatura de mai jos le compara.
  const semnatura = trasee.map(t => {
    const p = t.puncte || [];
    const u = p.length > 0 ? p[p.length - 1] : null;
    const c = t.pozitieCurenta;
    return `${t.id}:${p.length}:${u ? u.lat + ',' + u.lon : ''}:${c ? c.lat + ',' + c.lon : ''}`;
  }).join('|') + `|r${ruta ? ruta.length + ':' + ruta[0] + ':' + ruta[ruta.length - 1] : ''}|d${destinatie ? destinatie.lat + ',' + destinatie.lon : ''}`;

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerGroupRef.current;
    if (!map || !layerGroup) return;
    layerGroup.clearLayers();

    const toateCoordonatele = [];
    let ultimaPoz = null;
    let puncteVehicul = [];

    // Traseul recomandat pana la destinatie (doar ajutor vizual) - desenat INAINTEA traseului real,
    // ca urma parcursa sa ramana deasupra lui.
    if (ruta && ruta.length > 1) {
      L.polyline(ruta, { color: '#2980b9', weight: 5, opacity: 0.6, dashArray: '10,8' }).addTo(layerGroup);
      ruta.forEach(c => toateCoordonatele.push(c));
    }
    if (destinatie && destinatie.lat != null && destinatie.lon != null) {
      L.marker([destinatie.lat, destinatie.lon], {
        icon: L.divIcon({ className: '', html: '<div style="font-size:26px;line-height:26px;text-shadow:0 0 3px #fff">🏁</div>', iconSize: [26, 26], iconAnchor: [4, 24] }),
      }).addTo(layerGroup).bindTooltip(destinatie.nume ? destinatie.nume.split(',').slice(0, 2).join(',') : 'Destinație', { direction: 'top' });
      toateCoordonatele.push([destinatie.lat, destinatie.lon]);
    }
    trasee.forEach((t, index) => {
      const puncte = (t.puncte || []).filter(p => p.lat != null && p.lon != null);
      if (puncte.length === 0) return;
      const latlngs = puncte.map(p => [p.lat, p.lon]);
      latlngs.forEach(c => toateCoordonatele.push(c));

      // Culoare stabila per vehicul (nu per pozitie in lista) - vezi culoarePentru mai
      // sus. t.culoare ramane un override explicit, daca vreun apelant chiar vrea o
      // culoare fixa (ex. o singura cursa afisata izolat).
      const culoare = t.culoare || culoarePentru(t.eticheta || String(t.id));

      const { bucati, goluri } = imparteLaGoluri(puncte);
      bucati.forEach(b => { if (b.length > 0) L.polyline(b, { color: culoare, weight: 4, opacity: 0.8 }).addTo(layerGroup); });
      goluri.forEach(g => L.polyline(g, { color: culoare, weight: 3, opacity: 0.55, dashArray: '2,10' }).addTo(layerGroup));
      L.marker(latlngs[0], { icon: ICON_START }).addTo(layerGroup)
        .bindTooltip(`Start${t.eticheta ? ' — ' + t.eticheta : ''}`, { direction: 'top' });

      const pozitieCurenta = (index === 0 && liveRef.current) || t.pozitieCurenta || puncte[puncte.length - 1];
      // Cand exista pozitie live, cursorul e markerul live (mutat separat, fara redesenare) - nu-l dublam aici.
      if (pozitieCurenta && !(index === 0 && liveRef.current)) {
        const mk = L.marker([pozitieCurenta.lat, pozitieCurenta.lon], { icon: creazaIcon(culoare) }).addTo(layerGroup)
          .bindTooltip(t.eticheta || 'Vehicul', { direction: 'top', permanent: !!t.eticheta });
        if (index === 0) markerTraseuRef.current = mk;
      }
      if (index === 0) { // modul navigatie urmareste primul traseu (cursa soferului)
        ultimaPoz = pozitieCurenta;
        puncteVehicul = puncte;
      }
    });

    dateRef.current = { toate: toateCoordonatele, ultimaPoz, puncteVehicul };
    aplicaVedere();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semnatura]);

  // Cursor fluid: la fiecare citire GPS noua din telefon mutam DOAR markerul (setLatLng) si, in modul
  // navigatie cu urmarire, centram harta. Nu redesenam traseul si nu apelam serverul.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    liveRef.current = pozitieLive;
    if (!pozitieLive) {
      if (liveMarkerRef.current) { liveMarkerRef.current.remove(); liveMarkerRef.current = null; }
      return;
    }
    const ll = [pozitieLive.lat, pozitieLive.lon];
    if (markerTraseuRef.current) { markerTraseuRef.current.remove(); markerTraseuRef.current = null; } // evitam dublura
    if (!liveMarkerRef.current) {
      const t0 = trasee[0] || {};
      const culoare = t0.culoare || culoarePentru(t0.eticheta || String(t0.id));
      liveMarkerRef.current = L.marker(ll, { icon: creazaIcon(culoare), zIndexOffset: 1000 }).addTo(map);
    } else {
      liveMarkerRef.current.setLatLng(ll);
    }
    dateRef.current = { ...dateRef.current, ultimaPoz: pozitieLive };
    if (modNavRef.current && urmaresteRef.current) aplicaVedere();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pozitieLive]);

  function comutaMod() {
    const map = mapRef.current;
    if (!map) return;
    const nou = !modNavRef.current;
    seteazaModNav(nou);
    if (nou) {
      seteazaUrmareste(true);
      zoomNavSetatRef.current = false;
    } else {
      if (typeof map.setBearing === 'function') map.setBearing(0);
      directieRef.current = null;
      autoFitRef.current = true;
    }
    aplicaVedere();
  }

  function recentreaza() {
    if (modNavRef.current) {
      seteazaUrmareste(true);
    } else {
      autoFitRef.current = true;
    }
    aplicaVedere();
  }

  return (
    <div style={plin
      ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5000, background: '#fff' }
      : { position: 'relative' }}>
      <div ref={containerRef} style={{
        width: '100%', height: plin ? '100%' : inaltime,
        borderRadius: plin ? 0 : 6, border: plin ? 'none' : '1px solid #ccc',
      }} />
      <div style={{
        position: 'absolute', top: 8, right: 8, zIndex: 1200,
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end',
      }}>
        <button type="button" style={STIL_BUTON} onClick={() => setPlin(p => !p)}>
          {plin ? '✕ Închide' : '⛶ Ecran complet'}
        </button>
        {permiteNavigatie && (
          <button type="button" style={STIL_BUTON} onClick={comutaMod}>
            {modNav ? '🧭 Nord sus' : '🧭 Navigație'}
          </button>
        )}
        {!modNav && (
          <button type="button" style={STIL_BUTON} onClick={recentreaza}>⤢ Încadrează traseul</button>
        )}
        {modNav && !urmareste && (
          <button type="button" style={STIL_BUTON} onClick={recentreaza}>◎ Recentrează</button>
        )}
      </div>
      {plin && suprapunere && (
        // bara cu distanta / starea / Stop; ridicata putin ca sa nu acopere atribuirea OpenStreetMap
        <div style={{ position: 'absolute', left: 8, right: 8, bottom: 26, zIndex: 1300 }}>{suprapunere}</div>
      )}
    </div>
  );
}
